import type { TAutocompleteOption } from '~/components/AutocompleteField/types.ts';
import {
  GuaranteeOperationType,
  GuaranteeSecurityType,
  GuaranteesBankCommissionsRule,
  GuaranteesDeliveryMethod,
  GuaranteesEffectiveFromType,
  GuaranteesFormRule,
  GuaranteesPrincipalObligationDetails,
  GuaranteesSubordinationRule,
  type TGuaranteesBeneficiary,
  type TGuaranteesFormValues,
  type TGuaranteesPrincipal,
} from '~/store/guarantees/types.ts';
import {
  GuaranteesPrincipalConstituentDocumentsChangesOption,
  GuaranteesBeneficiaryFieldName,
  GuaranteesFormFieldName,
  GuaranteesPrincipalFieldName,
  type TGuaranteesRootSectionFormValues,
} from './types.ts';

export const DEFAULT_GUARANTEE_CURRENCY = 'UAH';
export const NON_RESIDENT_BENEFICIARY_IDENTIFY_CODE = '000000000';
export const BENEFICIARY_NAME_GUARANTEE_LANG_MAX_LENGTH = 38;

export const GUARANTEE_OPERATION_TYPE_OPTIONS: Array<
  TAutocompleteOption & { value: GuaranteeOperationType }
> = [
  { value: GuaranteeOperationType.TENDER, label: 'Тендерна' },
  { value: GuaranteeOperationType.TENDER_PROZORRO, label: 'Тендерна - Prozorro' },
  { value: GuaranteeOperationType.PERFORMANCE, label: "Виконання зобов'язань" },
  {
    value: GuaranteeOperationType.PERFORMANCE_PROZORRO,
    label: 'Виконання зобов’язань — Prozorro',
  },
  { value: GuaranteeOperationType.ADVANCE_PAYMENT, label: 'Авансового платежу' },
  { value: GuaranteeOperationType.PAYMENT, label: 'Платіжна' },
  { value: GuaranteeOperationType.OTHER, label: 'Інше' },
];

export const GUARANTEE_CURRENCY_OPTIONS: TAutocompleteOption[] = [
  { value: 'UAH', label: 'UAH' },
  { value: 'USD', label: 'USD' },
  { value: 'EUR', label: 'EUR' },
];

export const GUARANTEE_ALLOWED_CURRENCY_CODES = new Set([
  'UAH',
  'AUD',
  'BGN',
  'KRW',
  'HKD',
  'DKK',
  'USD',
  'PLN',
  'EUR',
  'JPY',
  'CAD',
  'HRK',
  'MXN',
  'NZD',
  'ILS',
  'NOK',
  'SGD',
  'ZAR',
  'RON',
  'HUF',
  'GBP',
  'CZK',
  'SEK',
  'CHF',
  'CNY',
]);

export const GUARANTEE_SECURITY_TYPE_OPTIONS: Array<
  TAutocompleteOption & {
    value: GuaranteeSecurityType;
  }
> = [
  {
    value: GuaranteeSecurityType.NO_SECURITY,
    label: 'Без забезпечення',
  },
  {
    value: GuaranteeSecurityType.COVERAGE_ACCOUNT,
    label: 'Розміщення грошового покриття на рахунку покриття',
  },
  {
    value: GuaranteeSecurityType.DEPOSIT_RIGHTS,
    label:
      'Забезпечення прав вимоги на грошові кошти, розміщені на вкладному (депозитному) рахунку',
  },
];

export const GUARANTEE_SECURITY_TYPE_UI_OPTIONS = GUARANTEE_SECURITY_TYPE_OPTIONS.filter(
  (option) => option.value !== GuaranteeSecurityType.NO_SECURITY
);

export const ROOT_SECTION_FIELD_NAMES = [
  'guaranteeOperationType',
  'guaranteeOperationTypeOther',
  'guaranteeAmount',
  'guaranteeCurrency',
  'guaranteeExpiryDate',
  'isUrgent',
] as const satisfies readonly (keyof TGuaranteesRootSectionFormValues)[];

export const MAIN_DEAL_FIELD_NAMES = [
  'contract',
  'mainDealId',
  'mainDealNumber',
  'mainDealDate',
  'mainDealDateFrom',
  'mainDealDateTo',
  'mainDealCurrency',
  'mainDealAmount',
  'mainDealLimitBalance',
] as const satisfies readonly (keyof TGuaranteesRootSectionFormValues)[];

export const PRINCIPAL_CONSTITUENT_DOCUMENTS_CHANGES_OPTIONS: Array<
  TAutocompleteOption & { value: GuaranteesPrincipalConstituentDocumentsChangesOption }
> = [
  { value: GuaranteesPrincipalConstituentDocumentsChangesOption.YES, label: 'Так' },
  { value: GuaranteesPrincipalConstituentDocumentsChangesOption.NO, label: 'Ні' },
];

export const PRINCIPAL_REQUIRED_FIELDS: Array<keyof TGuaranteesPrincipal> = [
  GuaranteesPrincipalFieldName.PrincipalIdentifyCode,
  GuaranteesPrincipalFieldName.PrincipalFullName,
  GuaranteesPrincipalFieldName.PrincipalShortName,
  GuaranteesPrincipalFieldName.PrincipalLocation,
  GuaranteesPrincipalFieldName.PrincipalContactFullName,
  GuaranteesPrincipalFieldName.PrincipalContactEmail,
  GuaranteesPrincipalFieldName.PrincipalContactPhone,
  GuaranteesPrincipalFieldName.PrincipalAuthorizedPersonPosition,
  GuaranteesPrincipalFieldName.PrincipalAuthorizedPersonName,
];

export const BENEFICIARY_REQUIRED_FIELDS: Array<keyof TGuaranteesBeneficiary> = [
  GuaranteesBeneficiaryFieldName.BeneficiaryName,
  GuaranteesBeneficiaryFieldName.BeneficiaryIdentifyCode,
  GuaranteesBeneficiaryFieldName.BeneficiaryLocation,
  GuaranteesBeneficiaryFieldName.BeneficiaryBank,
  GuaranteesBeneficiaryFieldName.BeneficiaryResidenceCountry,
];

export const BASE_RELATIONS_PROZORRO_VALUE = 'значення з Прозоро';

export const BASE_RELATIONS_TEXT = {
  sectionTitle: 'Базові відносини',
  sectionDescription: 'Перевірте та оновіть дані базових відносин для заявки.',
  sectionStepLabel: '07',
  relationsDocumentLabel: 'Документ базових відносин',
  principalObligationDetailsLabel: "Деталі невиконання зобов'язання принципалом",
  principalObligationDetailsPlaceholder: 'Оберіть деталі',
  loadingText: 'Завантаження даних базових відносин...',
  saveButton: 'Зберегти базові відносини',
} as const;

export const PRINCIPAL_OBLIGATION_DETAILS_OPTIONS: Array<
  TAutocompleteOption & { value: GuaranteesPrincipalObligationDetails }
> = [
  {
    value: GuaranteesPrincipalObligationDetails.PRINCIPAL_NOT_FULFILLED,
    label:
      'Принципал не виконав зобов’язання за Документом, на підставі якого виникають Базові відносини',
  },
  {
    value: GuaranteesPrincipalObligationDetails.PRINCIPAL_IMPROPERLY_FULFILLED,
    label:
      'Принципал неналежно виконав зобов’язання за Документом, на підставі якого виникають Базові відносини',
  },
  {
    value: GuaranteesPrincipalObligationDetails.CONTRACT_TERMINATED_DUE_TO_PRINCIPAL_ACTIONS,
    label:
      'Внаслідок протиправних дій Принципала відбулось розірвання (припинення дії) Документу, на підставі якого виникають Базові відносини',
  },
];

export const GUARANTEE_FORM_TEXT = {
  sectionTitle: 'Форма гарантії',
  sectionDescription: 'Перевірте та оновіть дані форми гарантії для заявки.',
  sectionStepLabel: '08',
  issuePlaceLabel: 'Місце видачі гарантії',
  issueDateLabel: 'Дата видачі гарантії',
  documentsLanguageLabel: 'Мова документів',
  demandPresentationFormLabel: 'Форма Гарантії/Погоджений текст гарантії',
  demandPresentationPlaceLabel: 'Місце подання вимоги',
  supportingDocumentsLabel:
    'Додаткові документи, які мають супроводжувати вимогу за гарантією',
  effectiveFromTypeLabel: 'Набуття чинності гарантії',
  effectiveFromTypePlaceholder: 'Оберіть набуття чинності гарантії',
  effectiveFromDateLabel: 'Набуття чинності гарантії',
  subordinationRuleLabel: 'Підпорядкування гарантії',
  subordinationRulePlaceholder: 'Оберіть правило',
  subordinationRuleOtherLabel: 'Інше підпорядкування гарантії',
  deliveryMethodLabel: 'Передача гарантії',
  deliveryMethodPlaceholder: 'Оберіть спосіб',
  deliveryMethodOtherLabel: 'Інший спосіб передачі гарантії',
  guaranteeFormRuleLabel: 'Форма гарантії',
  guaranteeFormRulePlaceholder: 'Оберіть форму гарантії',
  guaranteeFormRuleOtherLabel: 'Інша форма гарантії',
  bankCommissionsRuleLabel: 'Банківські комісії',
  bankCommissionsRulePlaceholder: 'Оберіть правило комісій',
  bankCommissionsRuleOtherLabel: 'Інше правило комісій',
  loadingText: 'Завантаження даних форми гарантії...',
  saveButton: 'Зберегти форму гарантії',
} as const;

export const GUARANTEE_FORM_EFFECTIVE_FROM_TYPE_OPTIONS: Array<
  TAutocompleteOption & { value: GuaranteesEffectiveFromType }
> = [
  { value: GuaranteesEffectiveFromType.ISSUE_DATE, label: 'Дата видачі' },
  { value: GuaranteesEffectiveFromType.OTHER_DATE, label: 'Інша дата' },
];

export const GUARANTEE_FORM_SUBORDINATION_RULE_OPTIONS: Array<
  TAutocompleteOption & { value: GuaranteesSubordinationRule }
> = [
  { value: GuaranteesSubordinationRule.UKRAINIAN_LAW, label: 'Право України' },
  { value: GuaranteesSubordinationRule.URDG_758, label: 'URDG 758' },
  { value: GuaranteesSubordinationRule.OTHER, label: 'Інше' },
];

export const GUARANTEE_FORM_DELIVERY_METHOD_OPTIONS: Array<
  TAutocompleteOption & { value: GuaranteesDeliveryMethod }
> = [
  { value: GuaranteesDeliveryMethod.PAPER_ORIGINAL, label: 'Паперовий оригінал' },
  { value: GuaranteesDeliveryMethod.SWIFT, label: 'SWIFT' },
  { value: GuaranteesDeliveryMethod.NBU_EMAIL, label: 'Електронна пошта НБУ' },
  { value: GuaranteesDeliveryMethod.PRINCIPAL_EMAIL, label: 'Електронна пошта принципала' },
  { value: GuaranteesDeliveryMethod.OTHER, label: 'Інше' },
];

export const GUARANTEE_FORM_RULE_OPTIONS: Array<
  TAutocompleteOption & { value: GuaranteesFormRule }
> = [
  { value: GuaranteesFormRule.APPLICATION_P7, label: 'Заява P7' },
  { value: GuaranteesFormRule.OTHER, label: 'Інше' },
];

export const GUARANTEE_FORM_BANK_COMMISSIONS_RULE_OPTIONS: Array<
  TAutocompleteOption & { value: GuaranteesBankCommissionsRule }
> = [
  { value: GuaranteesBankCommissionsRule.PRINCIPAL_PAYS_ALL, label: 'Принципал сплачує всі комісії' },
  {
    value: GuaranteesBankCommissionsRule.DEBIT_PRINCIPAL_ACCOUNT,
    label: 'Списати з рахунку принципала',
  },
  { value: GuaranteesBankCommissionsRule.OTHER, label: 'Інше' },
];

export const GUARANTEE_FORM_REQUIRED_FIELDS: Array<keyof TGuaranteesFormValues> = [
  GuaranteesFormFieldName.DeliveryMethod,
  GuaranteesFormFieldName.GuaranteeFormRule,
  GuaranteesFormFieldName.BankCommissionsRule,
];
